import React, { useState, useMemo } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useCreatePhantom } from '../../features/bobins/hooks/usePhantoms';
import { useDrums } from '../../features/drums/hooks/useDrums.js';

const PHASE_OPTIONS = [
  { value: 'unwinding', label: 'Unwinding' },
  { value: 'pre_stranding', label: 'Pre-Stranding' },
  { value: 'conductor_isolation', label: 'Conductor Isolation' },
  { value: 'rope', label: 'Rope' },
  { value: 'interior_sheat', label: 'Interior Sheath' },
  { value: 'exterior_sheat', label: 'Exterior Sheath' }
];

const WIRE_TYPES = ['SINGLE-WIRE', 'MULTI-WIRE', 'STRANDED'];
const COLORS = ['GREEN-YELLOW', 'BLUE', 'BROWN', 'BLACK', 'RED', 'WHITE', 'GREY', 'GREY 7004', 'NATUR'];

const AddPhantomTokenModal = ({ show, onHide }) => {
  const createMutation = useCreatePhantom();
  const { data: drumsData } = useDrums();
  const allDrums = drumsData?.data || [];

  const DRUM_MATERIALS = useMemo(
    () => [...new Set(allDrums.map(d => d.drumType))],
    [allDrums]
  );
  const DRUM_DIAMETERS = useMemo(
    () => [...new Set(allDrums.map(d => d.drumDiameter))].sort((a, b) => a - b),
    [allDrums]
  );

  const [form, setForm] = useState({
    phase: 'conductor_isolation',
    availableLength: '',
    wireType: 'MULTI-WIRE',
    wireDiameter: '',
    numberOfWires: '',
    section: '',
    color: '',
    density: '8.96',
    drum_material: '',
    drum_diameter: '',
    note: ''
  });

  const [error, setError] = useState('');

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const needsColor = form.phase === 'conductor_isolation';
  const needsWire = ['unwinding', 'pre_stranding', 'conductor_isolation'].includes(form.phase);
  // section is relevant for every phase (it's saved optionally → null when blank)
  const needsSection = true;

  const handleSubmit = async () => {
    setError('');

    if (!form.availableLength || Number(form.availableLength) <= 0) {
      setError('Available length must be > 0');
      return;
    }

    if (needsWire && !form.wireDiameter) {
      setError('Wire diameter is required for this phase');
      return;
    }

    if (needsColor && !form.color) {
      setError('Color is required for conductor isolation');
      return;
    }

    // parse wireDiameter — support comma-separated for multi-wire
    let wireDiameter = form.wireDiameter;
    let numberOfWires = form.numberOfWires;
    if (form.wireDiameter.includes(',')) {
      wireDiameter = form.wireDiameter.split(',').map(s => Number(s.trim())).filter(Boolean);
      numberOfWires = form.numberOfWires
        ? form.numberOfWires.split(',').map(s => Number(s.trim())).filter(Boolean)
        : wireDiameter.map(() => 1);
    } else {
      wireDiameter = Number(form.wireDiameter) || null;
      numberOfWires = Number(form.numberOfWires) || null;
    }

    try {
      await createMutation.mutateAsync({
        phase: form.phase,
        availableLength: Number(form.availableLength),
        material: 'COPPER',
        wireType: form.wireType,
        wireDiameter,
        numberOfWires,
        section: form.section ? Number(form.section) : null,
        color: needsColor ? form.color : null,
        density: Number(form.density) || 8.96,
        drum_material: form.drum_material || null,
        drum_diameter: form.drum_diameter ? Number(form.drum_diameter) : null,
        note: form.note || null
      });

      onHide();
      setForm({
        phase: 'conductor_isolation',
        availableLength: '',
        wireType: 'MULTI-WIRE',
        wireDiameter: '',
        numberOfWires: '',
        section: '',
        color: '',
        density: '8.96',
        drum_material: '',
        drum_diameter: '',
        note: ''
      });
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Failed to create phantom token');
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-body-tertiary">
        <Modal.Title>Add Phantom Token</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-3">
          A phantom token represents leftover material on a bobbin from a
          previous production run that can be reused in future launches.
        </p>

        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-3">
          <Col md={6}>
            <Form.Group>
              <Form.Label>Phase</Form.Label>
              <Form.Select
                value={form.phase}
                onChange={e => update('phase', e.target.value)}
              >
                {PHASE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Available Length (m) *</Form.Label>
              <Form.Control
                type="number"
                value={form.availableLength}
                onChange={e => update('availableLength', e.target.value)}
                placeholder="e.g. 350"
              />
            </Form.Group>
          </Col>

          {needsWire && (
            <>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>Wire Type</Form.Label>
                  <Form.Select
                    value={form.wireType}
                    onChange={e => update('wireType', e.target.value)}
                  >
                    {WIRE_TYPES.map(t => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>
                    Wire Diameter (mm) *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={form.wireDiameter}
                    onChange={e => update('wireDiameter', e.target.value)}
                    placeholder="1.70 or 1.35,1.70"
                  />
                  <Form.Text className="text-muted">
                    Comma-separated for multi-wire
                  </Form.Text>
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group>
                  <Form.Label>Number of Wires</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.numberOfWires}
                    onChange={e => update('numberOfWires', e.target.value)}
                    placeholder="1,6"
                  />
                  <Form.Text className="text-muted">
                    Comma-separated, matching diameters
                  </Form.Text>
                </Form.Group>
              </Col>
            </>
          )}

          {needsColor && (
            <Col md={4}>
              <Form.Group>
                <Form.Label>Isolation Color *</Form.Label>
                <Form.Select
                  value={form.color}
                  onChange={e => update('color', e.target.value)}
                >
                  <option value="">Select</option>
                  {COLORS.map(c => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          )}

          {needsSection && (
            <Col md={4}>
              <Form.Group>
                <Form.Label>Section (mm2)</Form.Label>
                <Form.Control
                  type="number"
                  step="0.01"
                  value={form.section}
                  onChange={e => update('section', e.target.value)}
                  placeholder="2.5"
                />
              </Form.Group>
            </Col>
          )}

          <Col md={4}>
            <Form.Group>
              <Form.Label>Density (g/cm3)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                value={form.density}
                onChange={e => update('density', e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Drum Material</Form.Label>
              <Form.Select
                value={form.drum_material}
                onChange={e => update('drum_material', e.target.value)}
              >
                <option value="">Select</option>
                {DRUM_MATERIALS.map(m => (
                  <option key={m} value={m}>
                    {m.charAt(0).toUpperCase() + m.slice(1)}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={4}>
            <Form.Group>
              <Form.Label>Drum Diameter (mm)</Form.Label>
              <Form.Select
                value={form.drum_diameter}
                onChange={e => update('drum_diameter', e.target.value)}
              >
                <option value="">Select</option>
                {DRUM_DIAMETERS.map(d => (
                  <option key={d} value={d}>
                    {d} mm
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>Note</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.note}
                onChange={e => update('note', e.target.value)}
                placeholder="e.g. leftover from JOB-1711234, can reuse for 2.5mm products"
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? 'Creating...' : 'Create Phantom Token'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddPhantomTokenModal;
